import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared account-creation state machine used by both the standalone
 * sign-up page (/auth/register) and the camp registration guest checkout.
 *
 * Handles:
 *   - choice of verification channel ('phone' | 'email')
 *   - OTP send (email route or SMS route)
 *   - OTP verify (email inline, SMS via PhoneVerificationModal)
 *   - POST /api/auth/register/  (the backend flags phone_verified=true when a
 *     recent SMS code has been consumed, so SMS-first signups are verified out
 *     of the gate)
 *
 * Usage (email path):
 *   const acc = useAccountCreation();
 *   await acc.startVerification({ email, phone, password, ... });
 *   // UI shows the email code input
 *   await acc.verifyEmailAndCreate({ code, ...fields, onSuccess })
 *
 * Usage (SMS path):
 *   const acc = useAccountCreation();
 *   await acc.startVerification({ email, phone, password, ... });
 *   // acc.showPhoneVerify is now true -> render <PhoneVerificationModal/>
 *   // on onVerified callback, call acc.createAccount({ ...fields })
 */
export default function useAccountCreation() {
  const [verifyMethod, setVerifyMethod] = useState('phone'); // 'phone' | 'email'

  // Email code UI state
  const [emailStep, setEmailStep] = useState('form');        // 'form' | 'verify'
  const [emailCode, setEmailCode] = useState('');

  // Phone modal toggle
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);

  // Common spinners
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  /**
   * Kicks off the OTP for the chosen channel.
   *
   * @param {Object} fields           Account fields; only `email` and `phone` are used here.
   * @returns {Promise<'phone' | 'email' | null>} resolved channel on success, null on failure.
   */
  const startVerification = async ({ email, phone }) => {
    if (verifyMethod === 'phone') {
      // Just flip the modal on. The PhoneVerificationModal itself takes care
      // of calling /api/auth/phone/send-code when user clicks "Send".
      setShowPhoneVerify(true);
      return 'phone';
    }

    // Email path
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/email/send-code/`, {
        email: email.toLowerCase(),
      });
      toast.success(`Verification code sent to ${res.data.email_masked || email}`);
      setEmailStep('verify');
      return 'email';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send verification code');
      return null;
    } finally {
      setSending(false);
    }
  };

  /**
   * Creates the account. Call this after the OTP has succeeded.
   * The backend auto-marks phone_verified=true when a recent matching SMS code
   * was consumed (see accounts/views.py register).
   */
  const createAccount = async (fields) => {
    const payload = {
      first_name: fields.first_name,
      last_name: fields.last_name,
      email: fields.email.toLowerCase(),
      phone: fields.phone || undefined,
      password: fields.password,
      role: 'parent',
      sms_consent: !!fields.sms_consent,
      referral_code: fields.referral_code || undefined,
    };
    const res = await axios.post(`${API_URL}/api/auth/register/`, payload);
    if (res.data.access_token) {
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('refreshToken', res.data.refresh_token);
    }
    return res.data;
  };

  /**
   * Email-path completion: verify the emailed code and then POST register.
   */
  const verifyEmailAndCreate = async ({ code, ...fields }) => {
    if ((code || emailCode).length !== 6) {
      toast.error('Please enter the 6-digit code');
      return null;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/auth/email/verify-code/`, {
        email: fields.email.toLowerCase(),
        code: code || emailCode,
      });
      const data = await createAccount(fields);
      toast.success('Account created! Welcome to Ready Set STEM!');
      return data;
    } catch (err) {
      const d = err.response?.data;
      let msg = d?.detail;
      if (!msg && d && typeof d === 'object') {
        msg = Object.entries(d)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
          .join(', ');
      }
      toast.error(msg || 'Registration failed');
      return null;
    } finally {
      setVerifying(false);
    }
  };

  return {
    // channel + UI state
    verifyMethod,
    setVerifyMethod,
    emailStep,
    setEmailStep,
    emailCode,
    setEmailCode,
    showPhoneVerify,
    setShowPhoneVerify,
    sending,
    verifying,

    // actions
    startVerification,
    createAccount,
    verifyEmailAndCreate,
  };
}
